import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiAlertTriangle, FiEdit3, FiTrash2 } from 'react-icons/fi';
import { createEndpoint, deleteEndpoint, EndpointFormValue, updateEndpoint } from './apiIntegrationService';
import { BodyFormat, Endpoint, EndpointParam, FieldType, IntegrationDetail } from './apiIntegrations.types';
import { exampleBodyText, isBodyParam } from './bodyExample';
import { methodClass } from './IntegrationDetailBody';
import './apiintegrations.css';

interface Props {
  detail: IntegrationDetail;
  warnings: string[];
  onChange: (detail: IntegrationDetail) => void;
  onBack: () => void;
  onNext: () => void;
}

const FIELD_TYPES = ['string', 'integer', 'number', 'boolean', 'array', 'object'] as const;
const PARAM_LOCATIONS = ['path', 'query', 'header', 'body', 'raw_body'] as const;
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const EMPTY_PARAM: EndpointParam = { name: '', type: 'string', location: 'query', description: '', required: false };
const BODY_FORMAT_LABELS: Record<BodyFormat, string> = { json: 'JSON', form: 'Form fields', multipart: 'File upload' };
const FILE_UPLOAD_NOTE = "File upload - agents can't send files yet, so this endpoint is left out of their tool.";

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Edit / Delete, styled like the rest of the app's item actions (Digital Workforce, Agents). */
const CardActions: React.FC<{ onEdit: () => void; onDelete: () => void }> = ({ onEdit, onDelete }) => (
  <>
    <button type="button" className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={onEdit}>
      <FiEdit3 size={12} aria-hidden="true" /> Edit
    </button>
    <button type="button" className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={onDelete}>
      <FiTrash2 size={12} aria-hidden="true" /> Delete
    </button>
  </>
);

/** What the parser wasn't sure about, one item per row. Long lists start folded. */
const ParseWarnings: React.FC<{ warnings: string[] }> = ({ warnings }) => {
  const FOLDED = 3;
  const [showAll, setShowAll] = useState(warnings.length <= FOLDED);
  const shown = showAll ? warnings : warnings.slice(0, FOLDED);
  return (
    <section className="ai-review-notes" aria-label="Things to double-check">
      <div className="ai-review-notes__head">
        <FiAlertTriangle size={15} aria-hidden="true" />
        <div>
          <div className="ai-review-notes__title">Double-check {warnings.length === 1 ? 'this' : `these ${warnings.length} things`} from the docs</div>
          <div className="ai-review-notes__sub">We weren't sure about these while reading your documentation. Fix anything that's wrong below, or on the Connect step.</div>
        </div>
      </div>
      <ol className="ai-review-notes__list">
        {shown.map((w, i) => <li key={i}>{w}</li>)}
      </ol>
      {warnings.length > FOLDED && (
        <button type="button" className="ai-toggle-link" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show fewer' : `Show all ${warnings.length}`}
        </button>
      )}
    </section>
  );
};

/** Chevron that collapses or expands one card. */
const CardToggle: React.FC<{ open: boolean; label: string; onToggle: () => void }> = ({ open, label, onToggle }) => (
  <button type="button" className="ai-chevron" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`} onClick={onToggle}>
    {open ? '▾' : '▸'}
  </button>
);

/** A section title that collapses or expands the whole section, with expand/collapse-all links. */
const SectionHeader: React.FC<{
  title: string; count: number; open: boolean; onToggle: () => void; onExpandAll: () => void; onCollapseAll: () => void;
}> = ({ title, count, open, onToggle, onExpandAll, onCollapseAll }) => (
  <div className="ai-section-header">
    <button type="button" className="ai-section-toggle" aria-expanded={open} onClick={onToggle}>
      <span aria-hidden="true">{open ? '▾' : '▸'}</span> {title} <span className="ai-section-count">{count}</span>
    </button>
    {open && count > 0 && (
      <span className="ai-section-links">
        <button type="button" className="ai-toggle-link" onClick={onExpandAll}>Expand all</button>
        <span aria-hidden="true">·</span>
        <button type="button" className="ai-toggle-link" onClick={onCollapseAll}>Collapse all</button>
      </span>
    )}
  </div>
);

/** Same confirm modal Skills use before a delete (MyTools.css mt-modal). */
const ConfirmDelete: React.FC<{ title: string; busy: boolean; onCancel: () => void; onConfirm: () => void }> = ({
  title, busy, onCancel, onConfirm,
}) => (
  <motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
    <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
      <div className="mt-modal__header">
        <div className="mt-modal__header-info">
          <div className="mt-modal__icon">🗑️</div>
          <h3 className="mt-modal__title">{title}</h3>
        </div>
        <button type="button" className="mt-modal__close" onClick={onCancel} disabled={busy} aria-label="Close">×</button>
      </div>
      <p className="mt-step-subtitle">This can't be undone.</p>
      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={onConfirm} disabled={busy}>
          {busy ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

const ParamTable: React.FC<{ params: EndpointParam[]; showLocation?: boolean }> = ({ params, showLocation }) => (
  <table className="ai-param-table">
    <thead>
      <tr><th>Param</th><th>Type</th>{showLocation && <th>Location</th>}<th>Required</th><th>Description</th></tr>
    </thead>
    <tbody>
      {params.map((p, i) => (
        <tr key={i}>
          <td>{p.name}{p.location === 'raw_body' && <span className="ai-mono"> (whole body)</span>}</td>
          <td>{p.type}</td>
          {showLocation && <td>{p.location}</td>}
          <td>{p.required ? 'Yes' : ''}</td>
          <td>{p.description}{p.enum_values?.length ? <span className="ai-mono"> ({p.enum_values.join(', ')})</span> : null}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** An endpoint's params split by where they're sent, plus an example of the body that goes out. */
const EndpointParams: React.FC<{ endpoint: Endpoint }> = ({ endpoint }) => {
  const [showExample, setShowExample] = useState(false);
  const format = endpoint.body_format || 'json';
  const urlParams = endpoint.params.filter(p => !isBodyParam(p));
  const bodyParams = endpoint.params.filter(isBodyParam);
  return (
    <>
      {urlParams.length > 0 && (
        <>
          <div className="ai-param-group">Sent in the URL and headers</div>
          <ParamTable params={urlParams} showLocation />
        </>
      )}
      {bodyParams.length > 0 && (
        <>
          <div className="ai-param-group">
            Sent in the body <span className="ai-param-group__format">{BODY_FORMAT_LABELS[format]}</span>
          </div>
          <ParamTable params={bodyParams} />
          {format !== 'multipart' && (
            <>
              <button type="button" className="ai-toggle-link" onClick={() => setShowExample(!showExample)} aria-expanded={showExample}>
                {showExample ? '▾' : '▸'} Example body
              </button>
              {showExample && <pre className="ai-code-box">{exampleBodyText(bodyParams, format)}</pre>}
            </>
          )}
        </>
      )}
    </>
  );
};

/** One endpoint - viewing, editing, or (endpoint.id < 0) adding a new one. */
const EndpointCard: React.FC<{
  endpoint: Endpoint; integrationId: number; startEditing?: boolean;
  expanded: boolean; onExpandedChange: (open: boolean) => void;
  onSaved: (e: Endpoint) => void; onDelete: () => void; onDiscardNew: () => void;
}> = ({ endpoint, integrationId, startEditing, expanded, onExpandedChange, onSaved, onDelete, onDiscardNew }) => {
  const isNew = endpoint.id < 0;
  const [editing, setEditing] = useState(!!startEditing);
  const [draft, setDraft] = useState(() => toDraft(endpoint));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toDraft(ep: Endpoint) {
    return {
      name: ep.name, description: ep.description, method: ep.http_method, pathTemplate: ep.path_template,
      returns: ep.returns_summary || '', bodyFormat: (ep.body_format || 'json') as BodyFormat, isIdempotent: ep.is_idempotent,
      params: ep.params.length ? ep.params : [{ ...EMPTY_PARAM }],
    };
  }

  const startEdit = () => {
    setDraft(toDraft(endpoint));
    setError(null);
    setEditing(true);
    onExpandedChange(true);
  };

  const cancel = () => {
    if (isNew) {
      onDiscardNew();
      return;
    }
    setDraft(toDraft(endpoint));
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const params = draft.params.filter(p => p.name.trim());
    const value: EndpointFormValue = {
      name: draft.name.trim(), description: draft.description.trim() || draft.name.trim(),
      http_method: draft.method, path_template: draft.pathTemplate.trim(),
      returns_summary: draft.returns.trim() || null, body_format: draft.bodyFormat, is_idempotent: draft.isIdempotent,
      timeout_seconds: endpoint.timeout_seconds, params,
    };
    try {
      let id = endpoint.id;
      if (isNew) id = (await createEndpoint(integrationId, value)).endpoint_id;
      else await updateEndpoint(integrationId, endpoint.id, value);
      onSaved({
        ...endpoint, id, name: value.name, description: value.description, http_method: draft.method,
        path_template: value.path_template, returns_summary: value.returns_summary, body_format: draft.bodyFormat,
        is_idempotent: draft.isIdempotent, params,
      });
      setEditing(false);
    } catch (err) {
      setError(errorText(err, "Couldn't save this endpoint."));
    } finally {
      setSaving(false);
    }
  };

  const setParam = (i: number, patch: Partial<EndpointParam>) =>
    setDraft({ ...draft, params: draft.params.map((p, j) => j === i ? { ...p, ...patch } : p) });

  if (!editing) {
    return (
      <div className="ai-card">
        <div className="ai-card__header">
          <div className="ai-card__lead">
            <CardToggle open={expanded} label={endpoint.name} onToggle={() => onExpandedChange(!expanded)} />
            <div>
              <div className="ai-card__title">
                <span className={methodClass(endpoint.http_method)}>{endpoint.http_method}</span>{' '}{endpoint.name}
              </div>
              <div className="ai-card__sub"><span className="ai-mono">{endpoint.path_template}</span> - {endpoint.description}</div>
              <div className="ai-card__returns">
                <span className="ai-card__returns-label">Returns</span>{' '}
                {endpoint.returns_summary
                  ? <span className="ai-mono">{endpoint.returns_summary}</span>
                  : <span className="ai-card__returns-empty">Not set</span>}
              </div>
              {endpoint.body_format === 'multipart' && <div className="ai-card__returns ai-warn-text">{FILE_UPLOAD_NOTE}</div>}
            </div>
          </div>
          <div className="ai-card__actions">
            <CardActions onEdit={startEdit} onDelete={onDelete} />
          </div>
        </div>
        {expanded && endpoint.params.length > 0 && <EndpointParams endpoint={endpoint} />}
      </div>
    );
  }

  return (
    <div className="ai-card">
      <div className="mt-field-row">
        <div className="mt-field mt-field--grow">
          <label className="mt-label">Name <span className="mt-required">*</span></label>
          <input className="mt-input" value={draft.name} placeholder="get_policy" onChange={e => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div className="mt-field">
          <label className="mt-label">Method</label>
          <select className="mt-select" value={draft.method} onChange={e => setDraft({ ...draft, method: e.target.value as Endpoint['http_method'] })}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-field mt-field--full">
        <label className="mt-label">Path <span className="mt-required">*</span></label>
        <input className="mt-input" value={draft.pathTemplate} onChange={e => setDraft({ ...draft, pathTemplate: e.target.value })} placeholder="/policies/{policyId}" />
      </div>
      <div className="mt-field mt-field--full">
        <label className="mt-label">Description</label>
        <input className="mt-input" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })}
          placeholder="What this endpoint does - the agent reads this to decide when to call it" />
      </div>
      <div className="mt-field mt-field--full">
        <label className="mt-label">Returns</label>
        <input className="mt-input" value={draft.returns} onChange={e => setDraft({ ...draft, returns: e.target.value })}
          placeholder="policy {id, number, insured.client_id, coverages[].limit}" />
        <span className="mt-field-hint">
          One line with the key fields that come back, especially ids other endpoints need. Use a.b for a field inside an
          object and list[].x for a field on each item of a list.
        </span>
      </div>
      {draft.method !== 'GET' && (
        <div className="mt-field mt-field--full">
          <label className="mt-label">Body sent as</label>
          <select className="mt-select" value={draft.bodyFormat} onChange={e => setDraft({ ...draft, bodyFormat: e.target.value as BodyFormat })}>
            <option value="json">JSON</option>
            <option value="form">Form fields (form-encoded)</option>
            <option value="multipart">File upload (agents can't use this yet)</option>
          </select>
          {draft.bodyFormat === 'multipart' && <span className="mt-field-hint">{FILE_UPLOAD_NOTE}</span>}
        </div>
      )}
      <label className="mt-toggle-row" style={{ gap: 8 }}>
        <input type="checkbox" checked={draft.isIdempotent} onChange={e => setDraft({ ...draft, isIdempotent: e.target.checked })} />
        Safe to retry (idempotent)
      </label>

      <table className="ai-param-table">
        <thead><tr><th>Param</th><th>Type</th><th>Location</th><th>Required</th><th>Description</th><th /></tr></thead>
        <tbody>
          {draft.params.map((p, i) => (
            <tr key={i}>
              <td><input className="mt-input" value={p.name} onChange={e => setParam(i, { name: e.target.value })} /></td>
              <td>
                <select className="mt-select" value={p.type} onChange={e => setParam(i, { type: e.target.value as FieldType })}>
                  {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </td>
              <td>
                <select className="mt-select" value={p.location} onChange={e => setParam(i, { location: e.target.value as EndpointParam['location'] })}>
                  {PARAM_LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </td>
              <td><input type="checkbox" checked={p.required} onChange={e => setParam(i, { required: e.target.checked })} /></td>
              <td><input className="mt-input" value={p.description || ''} onChange={e => setParam(i, { description: e.target.value })} /></td>
              <td>
                <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline mt-btn--sm" aria-label="Remove param"
                  onClick={() => setDraft({ ...draft, params: draft.params.filter((_, j) => j !== i) })}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" style={{ marginTop: 8 }}
        onClick={() => setDraft({ ...draft, params: [...draft.params, { ...EMPTY_PARAM }] })}>+ Add param</button>

      {error && <p className="ai-error" role="alert">{error}</p>}
      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={cancel} disabled={saving}>Cancel</button>
        <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" disabled={saving || !draft.name.trim() || !draft.pathTemplate.trim()} onClick={save}>
          {saving ? 'Saving…' : isNew ? 'Add endpoint' : 'Save'}
        </button>
      </div>
    </div>
  );
};

type PendingDelete = { id: number; name: string };

const EndpointsStep: React.FC<Props> = ({ detail, warnings, onChange, onBack, onNext }) => {
  const { integration, endpoints } = detail;
  const [newEndpoint, setNewEndpoint] = useState<Endpoint | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shownWarnings = warnings.length ? warnings : integration.parse_warnings ?? [];
  // The section and every endpoint start expanded.
  const [endpointsOpen, setEndpointsOpen] = useState(true);
  const [collapsedEndpoints, setCollapsedEndpoints] = useState<Set<number>>(new Set());
  const withId = (set: Set<number>, id: number, include: boolean) => {
    const next = new Set(set);
    if (include) next.add(id); else next.delete(id);
    return next;
  };

  const addEndpoint = () => {
    setEndpointsOpen(true);
    setNewEndpoint({
      id: -Date.now(), integration_id: integration.id, name: '', description: '', http_method: 'GET',
      path_template: '', is_idempotent: false, timeout_seconds: 20, params: [],
    });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteEndpoint(integration.id, pendingDelete.id);
      onChange({ ...detail, endpoints: endpoints.filter(e => e.id !== pendingDelete.id) });
      setPendingDelete(null);
    } catch (err) {
      setError(errorText(err, `Couldn't delete ${pendingDelete.name}.`));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <motion.div className="mt-step-content" key="integration-endpoints"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">Review endpoints</h2>
      <p className="mt-step-subtitle">
        This is what we found in the documentation for {integration.name}. Edit or remove anything that isn't right.
      </p>

      {shownWarnings.length > 0 && <ParseWarnings warnings={shownWarnings} />}

      {error && <p className="ai-error" role="alert">{error}</p>}

      <SectionHeader title="Endpoints" count={endpoints.length} open={endpointsOpen} onToggle={() => setEndpointsOpen(!endpointsOpen)}
        onExpandAll={() => setCollapsedEndpoints(new Set())} onCollapseAll={() => setCollapsedEndpoints(new Set(endpoints.map(e => e.id)))} />
      {endpointsOpen && (<>
        <p className="ai-section-intro">
          Each endpoint is one thing your agents can do with this API, like looking up a record or creating one. In a
          chat, the agent picks an endpoint by its <strong>description</strong> and reads each param's{' '}
          <strong>description</strong> and allowed values to fill it in, so those matter most. Required params must be
          filled in before the call is made. <strong>Returns</strong> is for people: it tells anyone reviewing or
          installing this what each endpoint gives back.
        </p>
        {endpoints.map(endpoint => (
          <EndpointCard key={endpoint.id} endpoint={endpoint} integrationId={integration.id}
            expanded={!collapsedEndpoints.has(endpoint.id)}
            onExpandedChange={open => setCollapsedEndpoints(prev => withId(prev, endpoint.id, !open))}
            onSaved={updated => onChange({ ...detail, endpoints: endpoints.map(e => e.id === updated.id ? updated : e) })}
            onDelete={() => setPendingDelete({ id: endpoint.id, name: endpoint.name })}
            onDiscardNew={() => undefined} />
        ))}
        {newEndpoint && (
          <EndpointCard key={newEndpoint.id} endpoint={newEndpoint} integrationId={integration.id} startEditing
            expanded onExpandedChange={() => undefined}
            onSaved={created => { onChange({ ...detail, endpoints: [...endpoints, created] }); setNewEndpoint(null); }}
            onDelete={() => setNewEndpoint(null)} onDiscardNew={() => setNewEndpoint(null)} />
        )}
        {!newEndpoint && (
          <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={addEndpoint}>+ Add endpoint</button>
        )}
      </>)}

      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack}>← Back</button>
        <button type="button" className="mt-btn mt-btn--primary" disabled={endpoints.length === 0} onClick={onNext}>Next →</button>
        {endpoints.length === 0 && <span className="mt-field-hint">Add at least one endpoint to continue</span>}
      </div>

      <AnimatePresence>
        {pendingDelete && (
          <ConfirmDelete title={`Delete "${pendingDelete.name}"?`} busy={deleting}
            onCancel={() => setPendingDelete(null)} onConfirm={confirmDelete} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EndpointsStep;
